import Phaser from 'phaser';

import { Player } from '../entities/Player';
import { WaveManager } from '../systems/WaveManager';
import { AutoAttack } from '../systems/AutoAttack';
import { UpgradeSystem } from '../systems/UpgradeSystem';
import { VFXSystem } from '../systems/VFXSystem';
import { getMetaProgression } from '../systems/MetaProgression';
import { BIOMES, Biome, getBiomeForWave, WAVES_PER_BIOME } from '../config/biomes';
import { audioManager } from '../audio/AudioManager';
import { t } from '../../i18n';
import { hapticError, setTelegramBackButton } from '../../telegram/webApp';
import { setDebugState } from '../debugHooks';

/** Collision radius added to the player's radius for hit detection. */
const COLLISION_EXTRA = 16;
/** Minimum ms between consecutive damage ticks from enemy contact. */
const DAMAGE_COOLDOWN_MS = 500;
const BIOME_FADE_MS = 600;
const PARTICLE_SPAWN_INTERVAL_MS = 400;
const DEATH_SLOWMO_SCALE = 0.35;
const HIT_HAPTIC_COOLDOWN_MS = 120;

export interface RunData {
  duration: number;      // seconds survived
  waveReached: number;
  enemiesKilled: number;
  coinsEarned: number;
}

export class GameScene extends Phaser.Scene {
  private player!: Player;
  private waveManager!: WaveManager;
  private autoAttack!: AutoAttack;
  private upgradeSystem!: UpgradeSystem;
  private vfx!: VFXSystem;

  private readonly joystickOrigin = new Phaser.Math.Vector2();
  private readonly joystickDirection = new Phaser.Math.Vector2();
  private joystickPointerId: number | null = null;

  private startTime = 0;
  private enemiesKilled = 0;
  private coinsEarned = 0;
  private runEnded = false;
  private lastDamageMs = 0;
  private readonly meta = getMetaProgression();

  private groundRect: any = null;
  private particleTimer: Phaser.Time.TimerEvent | null = null;
  private particleArcs: any[] = [];

  private timerText: Phaser.GameObjects.Text | null = null;
  private hpText: Phaser.GameObjects.Text | null = null;
  private coinsText: Phaser.GameObjects.Text | null = null;
  private killsText: Phaser.GameObjects.Text | null = null;
  private levelText: Phaser.GameObjects.Text | null = null;
  private muteBtn: Phaser.GameObjects.Text | null = null;
  private lastKnownLevel = 0;
  private lastHapticMs = 0;

  constructor() {
    super('Game');
  }

  create(): void {
    this.cameras.main.fadeIn(220, 0, 0, 0);
    // Start with forest biome colours
    this.cameras.main.setBackgroundColor(BIOMES[0].bgColor);

    this.startTime = Date.now();
    this.enemiesKilled = 0;
    this.coinsEarned = 0;
    this.runEnded = false;
    this.lastDamageMs = 0;
    this.lastHapticMs = 0;
    this.joystickPointerId = null;
    this.joystickDirection.set(0, 0);

    // Create VFX system first so it's available to entities via registry
    this.vfx = new VFXSystem(this);
    this.registry.set('vfx', this.vfx);

    this.player = new Player(this, this.scale.width / 2, this.scale.height / 2);
    this.applyMetaBonuses();

    this.waveManager = new WaveManager(this);
    this.waveManager.start();

    this.autoAttack = new AutoAttack(
      this,
      () => ({ x: this.player.x, y: this.player.y }),
      () => this.waveManager.getEnemies(),
      (enemy) => {
        this.waveManager.removeEnemy(enemy);
        this.enemiesKilled++;
        this.killsText?.setText(`☠ ${this.enemiesKilled}`);
        this.awardCoins(1);
      },
      () => this.player.stats.damage,
      () => this.player.stats.attackSpeed,
      () => this.player.stats.critChance,
    );

    this.upgradeSystem = new UpgradeSystem(this, this.player);
    const startingLevels = this.meta.getAppliedBonuses().extraStartingLevels;
    if (startingLevels > 0) {
      this.upgradeSystem.grantInstantLevels(startingLevels);
    }
    this.upgradeSystem.onResume(() => {
      // gameplay resumes automatically after card choice
    });

    // Coin pickup animation on boss defeat
    this.events.on('bossDefeated', () => {
      for (let i = 0; i < 3; i++) {
        const ox = (Math.random() - 0.5) * 60;
        const oy = (Math.random() - 0.5) * 60;
        this.time.delayedCall(i * 120, () => {
          this.vfx.coinPickupAnimation(
            this.player.x + ox,
            this.player.y + oy,
            { x: this.scale.width - 12, y: 52 },
          );
        });
      }
    });

    // HUD — timer centred at top
    this.timerText = this.add
      .text(this.scale.width / 2, 8, '0:00', {
        fontSize: '18px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(100);

    this.levelText = this.add
      .text(this.scale.width / 2, 30, 'Lvl 0', {
        fontSize: '14px',
        color: '#aaddff',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(100);

    // HUD — HP in top-left (below wave counter)
    this.hpText = this.add
      .text(12, 34, `${t('game.hp')}: ${this.player.stats.maxHp}`, {
        fontSize: '16px',
        color: '#ff6666',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setScrollFactor(0)
      .setDepth(100);

    this.coinsText = this.add
      .text(this.scale.width - 12, 10, `💰 ${this.coinsEarned}`, {
        fontSize: '16px',
        color: '#ffdd44',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(100);

    this.killsText = this.add
      .text(this.scale.width - 12, 32, `☠ ${this.enemiesKilled}`, {
        fontSize: '16px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(100);

    this.events.on('bossDefeated', (rewards?: { gold?: number }) => {
      this.awardCoins(rewards?.gold ?? 0);
    });

    this.input.on('pointerdown', this.handlePointerDown, this);
    this.input.on('pointermove', this.handlePointerMove, this);
    this.input.on('pointerup', this.handlePointerUp, this);
    this.input.on('pointerupoutside', this.handlePointerUp, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.cleanupRuntimeObjects, this);

    // Mute toggle button — bottom-right corner
    this.muteBtn = this.add
      .text(this.scale.width - 12, this.scale.height - 12, audioManager.isMuted ? '🔇' : '🔊', {
        fontSize: '24px',
      })
      .setOrigin(1, 1)
      .setScrollFactor(0)
      .setDepth(200)
      .setInteractive({ useHandCursor: true });

    this.muteBtn.on('pointerdown', () => {
      const muted = audioManager.toggleMute();
      this.muteBtn?.setText(muted ? t('game.mute.off') : t('game.mute.on'));
    });

    this.refreshHudTexts();

    // Telegram back button — end run and go to Menu
    setTelegramBackButton(() => {
      if (!this.runEnded) {
        this.endRun();
      }
    });
  }

  update(_time: number, delta: number): void {
    if (this.runEnded) return;

    // Update timer HUD
    const elapsed = Date.now() - this.startTime;
    const secs = Math.floor(elapsed / 1000);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    this.timerText?.setText(`${m}:${s.toString().padStart(2, '0')}`);
    setDebugState({ scene: 'Game', timer: `${m}:${s.toString().padStart(2, '0')}` });
    if (this.lastKnownLevel !== this.upgradeSystem.currentLevel) {
      this.lastKnownLevel = this.upgradeSystem.currentLevel;
      this.levelText?.setText(`Lvl ${this.lastKnownLevel}`);
    }

    // Skip gameplay updates while upgrade modal is open
    if (this.upgradeSystem.isOpen) return;

    this.player.update(delta, this.scale.width, this.scale.height);
    this.waveManager.update(this.player.x, this.player.y, delta);
    this.autoAttack.update(delta);

    const leveledUp = this.upgradeSystem.update(delta);
    if (leveledUp) {
      this.vfx.levelUpFlash();
    }

    // Player–enemy collision
    const now = Date.now();
    if (now - this.lastDamageMs >= DAMAGE_COOLDOWN_MS) {
      for (const enemy of this.waveManager.getEnemies()) {
        if (!enemy.active) continue;
        const dx = this.player.x - enemy.x;
        const dy = this.player.y - enemy.y;
        const hitRadius = 18 + COLLISION_EXTRA;
        if ((dx * dx + dy * dy) < hitRadius * hitRadius) {
          this.lastDamageMs = now;
          const died = this.player.takeDamage(enemy.damage);
          this.hpText?.setText(`${t('game.hp')}: ${Math.max(0, Math.floor(this.player.stats.hp))}`);
          // Screen shake + red damage number on player damage
          this.vfx.screenShake(8, 200);
          this.vfx.showDamageNumber(this.player.x, this.player.y - 30, enemy.damage, 'player');
          this.triggerHitHaptics(now);
          if (died) {
            this.endRun();
            return;
          }
          audioManager.play('playerDamage');
          break;
        }
      }
    }
  }

  private endRun(): void {
    if (this.runEnded) return;
    this.runEnded = true;

    audioManager.play('gameOver');

    const duration = Math.floor((Date.now() - this.startTime) / 1000);
    const runData: RunData = {
      duration,
      waveReached: this.waveManager.getCurrentWave(),
      enemiesKilled: this.enemiesKilled,
      coinsEarned: this.coinsEarned,
    };

    this.cleanupRuntimeObjects();

    const desaturate = this.add
      .rectangle(0, 0, this.scale.width, this.scale.height, 0x888888, 0)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(600);

    this.tweens.add({
      targets: desaturate,
      alpha: 0.55,
      duration: 260,
      ease: 'Linear',
    });
    this.time.timeScale = DEATH_SLOWMO_SCALE;
    this.tweens.timeScale = DEATH_SLOWMO_SCALE;
    this.cameras.main.fadeOut(420, 0, 0, 0);

    // Brief pause before results
    this.time.delayedCall(600, () => {
      this.time.timeScale = 1;
      this.tweens.timeScale = 1;
      this.scene.start('Results', runData);
    });
  }

  // ─── Biome transition ────────────────────────────────────────────────────

  private handleBiomeChange(biome: Biome): void {
    // Fade out → swap colours → fade back in
    this.cameras.main.fadeOut(BIOME_FADE_MS, 0, 0, 0, (_cam: Phaser.Cameras.Scene2D.Camera, progress: number) => {
      if (progress === 1) {
        this.applyBiomeColors(biome);
        this.cameras.main.fadeIn(BIOME_FADE_MS, 0, 0, 0);
        this.restartParticles(biome);
      }
    });
  }

  private applyBiomeColors(biome: Biome): void {
    this.cameras.main.setBackgroundColor(biome.bgColor);
    const groundHex = Phaser.Display.Color.HexStringToColor(biome.groundColor).color;
    this.groundRect.setFillStyle(groundHex);
  }

  // ─── Ambient particles ───────────────────────────────────────────────────

  private startParticles(biome: Biome): void {
    this.particleTimer = this.time.addEvent({
      delay: PARTICLE_SPAWN_INTERVAL_MS,
      callback: () => this.spawnParticle(biome),
      loop: true,
    });
  }

  private restartParticles(biome: Biome): void {
    this.particleTimer?.destroy();
    this.particleTimer = null;
    // Fade out existing particles quickly
    for (const arc of this.particleArcs) {
      this.tweens.add({ targets: arc, alpha: 0, duration: 200, onComplete: () => arc.destroy() });
    }
    this.particleArcs = [];
    this.startParticles(biome);
  }

  private spawnParticle(biome: Biome): void {
    const { width, height } = this.scale;

    let color: number;
    let startX: number;
    let startY: number;
    let endX: number;
    let endY: number;
    let radius: number;
    let alpha: number;

    switch (biome.ambientParticles) {
      case 'leaf':
        color = 0x55cc44;
        startX = Math.random() * width;
        startY = -8;
        endX = startX + (Math.random() - 0.5) * 120;
        endY = height + 8;
        radius = 4;
        alpha = 0.7;
        break;
      case 'rock':
        color = 0x888899;
        startX = Math.random() * width;
        startY = -8;
        endX = startX;
        endY = height + 8;
        radius = 3;
        alpha = 0.6;
        break;
      case 'fog':
        color = 0xbbaadd;
        startX = -12;
        startY = Math.random() * height;
        endX = width + 12;
        endY = startY + (Math.random() - 0.5) * 40;
        radius = 8;
        alpha = 0.3;
        break;
      case 'ember':
        color = 0xff6622;
        startX = Math.random() * width;
        startY = height + 8;
        endX = startX + (Math.random() - 0.5) * 80;
        endY = -8;
        radius = 3;
        alpha = 0.8;
        break;
      default:
        return;
    }

    const arc = this.add.arc(startX, startY, radius, 0, 360, false, color, alpha);
    arc.setDepth(1);
    this.particleArcs.push(arc);

    const duration = 3000 + Math.random() * 2000;
    this.tweens.add({
      targets: arc,
      x: endX,
      y: endY,
      alpha: 0,
      duration,
      ease: 'Linear',
      onComplete: () => {
        const idx = this.particleArcs.indexOf(arc);
        if (idx !== -1) this.particleArcs.splice(idx, 1);
        arc.destroy();
      },
    });
  }

  // ─── Input handlers ──────────────────────────────────────────────────────

  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    if (this.joystickPointerId !== null) return;
    this.joystickPointerId = pointer.id;
    this.joystickOrigin.set(pointer.x, pointer.y);
    this.joystickDirection.set(0, 0);
    this.player.setInput(0, 0);
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (this.joystickPointerId !== pointer.id || !pointer.isDown) return;
    const minSide = Math.min(this.scale.width, this.scale.height);
    const maxDistance = Phaser.Math.Clamp(minSide * 0.18, 56, 96);
    const deadZone = maxDistance * 0.15;
    const deadZoneSq = deadZone * deadZone;
    const dx = pointer.x - this.joystickOrigin.x;
    const dy = pointer.y - this.joystickOrigin.y;
    if ((dx * dx + dy * dy) < deadZoneSq) {
      this.joystickDirection.set(0, 0);
    } else {
      this.joystickDirection.set(dx / maxDistance, dy / maxDistance);
    }
    if (this.joystickDirection.lengthSq() > 1) {
      this.joystickDirection.normalize();
    }
    this.player.setInput(this.joystickDirection.x, this.joystickDirection.y);
  }

  private handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.joystickPointerId !== pointer.id) return;
    this.joystickPointerId = null;
    this.joystickDirection.set(0, 0);
    this.player.setInput(0, 0);
  }

  private applyMetaBonuses(): void {
    const bonuses = this.meta.getAppliedBonuses();
    this.player.stats.maxHp = Math.round(this.player.stats.maxHp * bonuses.maxHpMultiplier);
    this.player.stats.hp = this.player.stats.maxHp;
    this.player.stats.damage = Math.round(this.player.stats.damage * bonuses.startingDamageMultiplier);
    this.player.stats.speed = Math.round(this.player.stats.speed * bonuses.moveSpeedMultiplier);
  }

  private awardCoins(baseAmount: number): void {
    if (baseAmount <= 0) return;
    const bonusMultiplier = this.player.stats.goldBonus ?? 1;
    const amount = Math.max(1, Math.floor(baseAmount * bonusMultiplier));

    this.coinsEarned += amount;
    this.meta.addCoins(amount);
    this.coinsText?.setText(`💰 ${this.coinsEarned}`);
  }

  private refreshHudTexts(): void {
    this.hpText?.setText(`${t('game.hp')}: ${Math.max(0, Math.floor(this.player.stats.hp))}`);
    this.coinsText?.setText(`💰 ${this.coinsEarned}`);
    this.muteBtn?.setText(audioManager.isMuted ? t('game.mute.off') : t('game.mute.on'));
  }

  private triggerHitHaptics(now: number): void {
    if (now - this.lastHapticMs < HIT_HAPTIC_COOLDOWN_MS) return;
    this.lastHapticMs = now;

    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(20);
    }
    hapticError();
  }

  private cleanupRuntimeObjects(): void {
    this.waveManager?.destroy();
    this.autoAttack?.destroy();
    this.particleTimer?.destroy();
    this.particleTimer = null;
    this.particleArcs.forEach(arc => arc.destroy());
    this.particleArcs = [];

    this.joystickPointerId = null;
    this.joystickDirection.set(0, 0);
    this.player?.setInput(0, 0);

    this.input.off('pointerdown', this.handlePointerDown, this);
    this.input.off('pointermove', this.handlePointerMove, this);
    this.input.off('pointerup', this.handlePointerUp, this);
    this.input.off('pointerupoutside', this.handlePointerUp, this);
    this.events.removeAllListeners('bossDefeated');
  }
}
